import { FC, useState, useEffect, useCallback } from 'react';
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Settings48Regular, BinRecycle24Regular, PersonDelete24Regular, WeatherSunny24Regular, WeatherMoon24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../Contexts/NotificationContext';
import { useConfirm } from '../../Contexts/hooks';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { acquireAccessToken } from '../../Security/azureAdAuthentication';
import { appConfig } from '../../AppConfig';
import { useConversations, useTheme, useFeature, useUserPreferences } from '../../store/hooks';
import { clearConversationsAction } from '../../store/actions/conversationActions';
import { switchThemeAction } from '../../store/actions/themeActions';
import { setUserPreferencesAction } from '../../store/actions/userPreferencesAction';
import { LIGHT_THEME, DARK_THEME } from '../../store/models/theme';
import { IUserPreferences, UserPreferences } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { logout } from '../../Security/azureAdAuthentication';
import SettingsView from './SettingsView';

// Constants for voice entry types
const VOICE_ENTRY_TYPE_CLICK_SEND = 'Click Send';
const VOICE_ENTRY_TYPE_AUTOMATIC_SEND = 'Automatic Send';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.flex(1, 1, 'auto'),
        ...shorthands.padding('30px', '120px'),
        ...shorthands.gap('20px'),
        '@media (max-width: 768px)': {
            ...shorthands.padding('20px', '8px'),
        },
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
    settingsIcon: {
        color: tokens.colorBrandForeground1,
    },
    settingsTitle: {
        color: tokens.colorBrandForeground1,
    },
    settingsContent: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('20px'),
        ...shorthands.margin('2rem', '0', '0', '0'),
        maxWidth: '1000px',
        width: '100%',
    },
    settingsCard: {
        ...shorthands.padding('20px'),
    },
    settingsSection: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('15px'),
    },
    sectionTitle: {
        color: tokens.colorBrandForeground1,
        fontWeight: tokens.fontWeightSemibold,
    },
    sectionDescription: {
        color: tokens.colorNeutralForeground2,
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
        marginTop: tokens.spacingVerticalL,
    },
    actionButton:{
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        transition: 'all 0.3s ease-in-out',
        width: '100%',
        height: '48px',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        ':hover': {
            backgroundColor: tokens.colorBrandBackgroundHover,
        },
        ':active': {
            backgroundColor: tokens.colorBrandBackgroundPressed,
        },
        ':disabled': {
            backgroundColor: tokens.colorNeutralBackground4,
            color: tokens.colorNeutralForeground4,
            cursor: 'not-allowed',
        },
    },
    dangerActionButton: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        transition: 'all 0.3s ease-in-out',
        width: '100%',
        height: '48px',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        ':hover': {
            backgroundColor: tokens.colorBrandBackgroundHover,
        },
        ':active': {
            backgroundColor: tokens.colorBrandBackgroundPressed,
        },    
    },
    themeSettingContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        ...shorthands.padding(tokens.spacingVerticalL, 0),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    themeSettingLeft: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalXS),
    },
    themeSettingRight: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalM),
        '@media (max-width: 768px)': {
            ...shorthands.gap('2px'),
        },
    },
    themeIcon: {
        color: tokens.colorBrandForeground1,
    },
    themeIconLight: {
        color: tokens.colorNeutralForeground1, // Default color for light mode
    },
    themeIconDark: {
        color: '#FF6542', // Direct orange color for dark mode
    },
    themeSwitchLight: {
        // Default styling for light mode - use default Fluent UI colors
    },
    themeSwitchDark: {
        // Orange styling for dark mode - using !important to override default styling
        '& .fui-Switch__indicator': {
            backgroundColor: `${tokens.colorBrandBackground} !important`,
        },
        '& .fui-Switch__thumb': {
            backgroundColor: `${tokens.colorNeutralForegroundOnBrand} !important`,
        },
        '&[data-checked="true"] .fui-Switch__indicator': {
            backgroundColor: `${tokens.colorBrandBackground} !important`,
        },
        '&[data-checked="true"] .fui-Switch__thumb': {
            backgroundColor: `${tokens.colorNeutralForegroundOnBrand} !important`,
        },
        // Also target unchecked state to ensure orange is used in dark mode
        '&[data-checked="false"] .fui-Switch__indicator': {
            backgroundColor: `${tokens.colorBrandBackground} !important`,
        },
        '&[data-checked="false"] .fui-Switch__thumb': {
            backgroundColor: `${tokens.colorNeutralForegroundOnBrand} !important`,
        },
    },
    voiceSettingContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
        ...shorthands.padding(tokens.spacingVerticalL, 0),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    voiceSettingRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.gap(tokens.spacingHorizontalM),
        '@media (max-width: 480px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            ...shorthands.gap(tokens.spacingVerticalS),
        },
    },
    voiceSettingLeft: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalXS),
        flex: 1,
    },
    voiceSettingRight: {
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalXS),
        '@media (max-width: 480px)': {
            minWidth: 'auto',
            width: '100%',
        },
    },
    autoSendSecsInput: {
        width: '80px',
        maxWidth: '80px',
    },
    dividerContainer: {
        ...shorthands.margin(tokens.spacingVerticalM, 0),
    },
    errorText: {
        color: tokens.colorPaletteRedForeground1,
        fontSize: tokens.fontSizeBase200,
    },
});

const SettingsHandler: FC = () => {
    // Feature flags
    const DisenrollFromPlatformAvailable = false;
    
    const classes = useStyles();
    const { t } = useTranslation();
    const confirm = useConfirm();
    const { dispatchNotification } = useNotification();
    const { dispatch } = useConversations();
    const { theme, dispatch: themeDispatch } = useTheme();
    // Get chat feature for supported languages
    const chatFeature = useFeature('chat');
    const { userPreferences, dispatch: userPreferencesDispatch } = useUserPreferences();
    
    // Survey management feature flag from role-based configuration
    const isSurveyManagementEnabled = chatFeature?.conversation?.isUploadSurvey ?? false;
    
    // Get supported languages from chat feature
    let speechToTextSupportedLanguages = chatFeature?.speechToText?.languages || [];
    // Add Auto-detect as the first option if not already present
    if (!speechToTextSupportedLanguages.some((lang: any) => lang.locale === 'auto')) {
        speechToTextSupportedLanguages = [
            { locale: 'auto', displayLanguage: 'Auto-detection' },
            ...speechToTextSupportedLanguages,
        ];
    }
    const [selectedLanguage, setSelectedLanguage] = useState(
        userPreferences?.speechToTextLanguage || speechToTextSupportedLanguages[0]?.locale || 'en-US'
    );    const [voiceEntryType, setVoiceEntryType] = useState(
        userPreferences?.speechClickSend !== undefined 
            ? (userPreferences.speechClickSend ? VOICE_ENTRY_TYPE_CLICK_SEND : VOICE_ENTRY_TYPE_AUTOMATIC_SEND)
            : VOICE_ENTRY_TYPE_CLICK_SEND
    );
    const [autoSendNumSecs, setAutoSendNumSecs] = useState<number>(
        userPreferences?.speechAutoSendNumSecs || 3
    );
    const [autoSendNumSecsInput, setAutoSendNumSecsInput] = useState<string>(
        (userPreferences?.speechAutoSendNumSecs || 3).toString()
    );
    const [autoSendNumSecsError, setAutoSendNumSecsError] = useState<string>('');
    const [showLanguageSelectionInPrompt, setShowLanguageSelectionInPrompt] = useState<boolean>(
        userPreferences?.showLanguageSelectionInPrompt ?? true
    );

    // Survey management state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isSurveyActive, setIsSurveyActive] = useState<boolean>(false);
    const [currentSurvey, setCurrentSurvey] = useState<any>(null);
    const [isLoadingSurvey, setIsLoadingSurvey] = useState<boolean>(false);
    // Survey editing state
    const [editedSurveyJson, setEditedSurveyJson] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    // Using the correct configuration property for survey management
    // const surveyManagementEnabled = chatFeature?.survey?.isManagementEnabled ?? false;

    // Function to load survey based on active/inactive state
    const loadSurveyByMode = useCallback(async (showActive: boolean) => {
        setIsLoadingSurvey(true);
        try {
            const baseUrl = import.meta.env.VITE_MIDDLEWARE_API_BASE_URL;
            const accessToken = await acquireAccessToken(appConfig.MIDDLEWARE_API_SCOPES);
            
            // Use different endpoints based on mode
            const endpoint = showActive 
                ? `${baseUrl}/api/v1/survey/latest/raw`  // Active survey
                : `${baseUrl}/api/v1/survey/mostrecent`; // Most recent survey
            
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
            });

            if (response.ok) {
                const responseText = await response.text();
                if (responseText.trim() === '') {
                    setCurrentSurvey(null);
                } else {
                    try {
                        const survey = JSON.parse(responseText);
                        setCurrentSurvey(survey);
                    } catch (parseError) {
                        console.error('Error parsing survey JSON:', parseError);
                        setCurrentSurvey(null);
                    }
                }
            } else {
                setCurrentSurvey(null);
            }
        } catch (error) {
            console.error('Error loading survey data:', error);
            setCurrentSurvey(null);
        } finally {
            setIsLoadingSurvey(false);
        }
    }, []);

    // Function to load survey activation status and fetch correct survey
    const loadSurveyStatus = useCallback(async () => {
        setIsLoadingSurvey(true);
        try {
            const baseUrl = import.meta.env.VITE_MIDDLEWARE_API_BASE_URL;
            const accessToken = await acquireAccessToken(appConfig.MIDDLEWARE_API_SCOPES);
            
            // Check if there is an active survey
            const activeResponse = await fetch(`${baseUrl}/api/v1/survey/latest/raw`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
            });

            if (activeResponse.ok) {
                const activeText = await activeResponse.text();
                if (activeText.trim()) {
                    setIsSurveyActive(true);
                    setCurrentSurvey(JSON.parse(activeText));
                    setIsLoadingSurvey(false);
                    return;
                }
            }

            // If no active survey, get the most recent survey (inactive)
            const recentResponse = await fetch(`${baseUrl}/api/v1/survey/mostrecent`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
            });

            if (recentResponse.ok) {
                const recentText = await recentResponse.text();
                if (recentText.trim()) {
                    setIsSurveyActive(false);
                    setCurrentSurvey(JSON.parse(recentText));
                } else {
                    setIsSurveyActive(false);
                    setCurrentSurvey(null);
                }
            } else {
                setIsSurveyActive(false);
                setCurrentSurvey(null);
            }
        } catch (error) {
            setIsSurveyActive(false);
            setCurrentSurvey(null);
        } finally {
            setIsLoadingSurvey(false);
        }
    }, []);

    // Update local state when user preferences change
    useEffect(() => {
        if (userPreferences?.speechToTextLanguage) {
            setSelectedLanguage(userPreferences.speechToTextLanguage);
        }
        if (userPreferences?.speechAutoSendNumSecs != null) {
            setAutoSendNumSecs(userPreferences.speechAutoSendNumSecs);
            setAutoSendNumSecsInput(userPreferences.speechAutoSendNumSecs.toString());
        }        if (userPreferences?.speechClickSend !== undefined) {
            setVoiceEntryType(userPreferences.speechClickSend ? VOICE_ENTRY_TYPE_CLICK_SEND : VOICE_ENTRY_TYPE_AUTOMATIC_SEND);
        }
        if (userPreferences?.showLanguageSelectionInPrompt !== undefined) {
            setShowLanguageSelectionInPrompt(userPreferences.showLanguageSelectionInPrompt);
        }
    }, [
        userPreferences?.speechToTextLanguage,
        userPreferences?.speechAutoSendNumSecs,
        userPreferences?.speechClickSend,
        userPreferences?.showLanguageSelectionInPrompt
    ]);

    // Load survey status on component mount
    useEffect(() => {
        loadSurveyStatus();
    }, [loadSurveyStatus]);

    // Update editedSurveyJson when currentSurvey changes
    useEffect(() => {
        if (currentSurvey) {
            setEditedSurveyJson(JSON.stringify(currentSurvey, null, 2));
        } else {
            setEditedSurveyJson('');
        }
    }, [currentSurvey]);
    const voiceEntryTypes = [
        { key: VOICE_ENTRY_TYPE_CLICK_SEND, text: VOICE_ENTRY_TYPE_CLICK_SEND },
        { key: VOICE_ENTRY_TYPE_AUTOMATIC_SEND, text: VOICE_ENTRY_TYPE_AUTOMATIC_SEND },
    ];
    
    // Function to validate auto-send seconds input
    const validateAutoSendNumSecs = useCallback((value: string) => {
        if (value === '') {
            setAutoSendNumSecsError('');
            return false;
        }
        
        const numValue = parseInt(value);
        if (isNaN(numValue)) {
            setAutoSendNumSecsError(t('settings.voice.autoSendNumSecsError.invalid', 'Please enter a valid number'));
            return false;
        }
        
        if (numValue < 1) {
            setAutoSendNumSecsError(t('settings.voice.autoSendNumSecsError.tooLow', 'Minimum value is 1 second'));
            return false;
        }
        
        if (numValue > 5) {
            setAutoSendNumSecsError(t('settings.voice.autoSendNumSecsError.tooHigh', 'Maximum value is 5 seconds'));
            return false;
        }
        
        setAutoSendNumSecsError('');
        return true;
    }, [t]);
    
    // Function to update language preference
    const onUpdateUserPreferencesLanguage = useCallback(
        (locale: string) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setSelectedLanguage(locale);
                    // Update user preferences with the locale language
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        speechToTextLanguage: locale,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating language preference:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );
    // Function to update auto-send number of seconds preference
    const onUpdateAutoSendNumSecs = useCallback(
        (numSecs: number) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setAutoSendNumSecs(numSecs);
                    setAutoSendNumSecsInput(numSecs.toString());
                    // Update user preferences with the auto-send number of seconds
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        speechAutoSendNumSecs: numSecs,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating auto-send preference:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );

    // Function to update voice entry type preference (speechClickSend)
    const onUpdateVoiceEntryType = useCallback(
        (entryType: string) => {
            performEnterpriseGPTApiCall(async (client) => {                
                try {
                    setVoiceEntryType(entryType);
                    const speechClickSend = entryType === VOICE_ENTRY_TYPE_CLICK_SEND;
                    // Update user preferences with the voice entry type
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        speechClickSend: speechClickSend,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating voice entry type preference:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );

    // Function to update show/hide language selection preference
    const onUpdateShowLanguageSelectionInPrompt = useCallback(
        (show: boolean) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setShowLanguageSelectionInPrompt(show);
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        showLanguageSelectionInPrompt: show,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating show/hide language selection:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );

    // Function to handle survey JSON file upload
    const onFileUpload = useCallback(
        async (file: File) => {
            if (!file) return;

            // Validate file type
            if (!file.name.endsWith('.json')) {
                dispatchNotification('settings.survey.error.invalidFileType', 'error');
                return;
            }

            // Validate file size (max 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                dispatchNotification('settings.survey.error.fileTooLarge', 'error');
                return;
            }

            setIsUploading(true);

            try {
                // Read file content
                const fileContent = await file.text();
                
                // Validate JSON format
                let surveyData;
                try {
                    surveyData = JSON.parse(fileContent);
                } catch (parseError) {
                    dispatchNotification('settings.survey.error.invalidJSON', 'error');
                    setIsUploading(false);
                    return;
                }

                // Upload survey using the existing upsertSurvey API
                await performEnterpriseGPTApiCall(async (client) => {
                    await client.upsertSurvey(surveyData);
                    dispatchNotification('settings.survey.upload.success', 'success');
                });

                // Refresh the survey data to show the newly uploaded survey
                await loadSurveyStatus();

            } catch (error) {
                console.error('Error uploading survey:', error);
                dispatchNotification('settings.survey.error.uploadFailed', 'error');
            } finally {
                setIsUploading(false);
            }
        },
        [dispatchNotification, loadSurveyStatus],
    );

    // Function to save the edited survey JSON
    const onSaveSurvey = useCallback(
        async () => {
            if (!editedSurveyJson.trim()) {
                dispatchNotification('settings.survey.error.emptyContent', 'error');
                return;
            }

            setIsSaving(true);

            try {
                // Validate JSON format
                let surveyData;
                try {
                    surveyData = JSON.parse(editedSurveyJson);
                } catch (parseError) {
                    dispatchNotification('settings.survey.error.invalidJSON', 'error');
                    setIsSaving(false);
                    return;
                }

                // Remember if the survey was originally inactive
                const wasOriginallyInactive = !isSurveyActive;

                // Save survey using the existing upsertSurvey API
                // NOTE: This will unfortunately make the survey active temporarily
                await performEnterpriseGPTApiCall(async (client) => {
                    await client.upsertSurvey(surveyData);
                });

                // If the survey was originally inactive, deactivate it immediately
                if (wasOriginallyInactive && currentSurvey?.id) {
                    const baseUrl = import.meta.env.VITE_MIDDLEWARE_API_BASE_URL;
                    const accessToken = await acquireAccessToken(appConfig.MIDDLEWARE_API_SCOPES);
                    
                    await fetch(`${baseUrl}/api/v1/survey/deactivate`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                        },
                        body: JSON.stringify({ surveyId: currentSurvey.id }),
                    });
                }

                dispatchNotification('settings.survey.save.success', 'success');

                // After saving, reload the survey in the same mode (inactive)
                await loadSurveyByMode(false);

            } catch (error) {
                console.error('Error saving survey:', error);
                dispatchNotification('settings.survey.error.saveFailed', 'error');
            } finally {
                setIsSaving(false);
            }
        },
        [editedSurveyJson, dispatchNotification, loadSurveyByMode, isSurveyActive, currentSurvey],
    );

    // Function to save survey directly from visual editor with provided JSON
    const onSaveSurveyDirect = useCallback(
        async (surveyJsonString: string) => {
            if (!surveyJsonString.trim()) {
                dispatchNotification('settings.survey.error.emptyContent', 'error');
                return;
            }

            setIsSaving(true);

            try {
                // Validate JSON format
                let surveyData;
                try {
                    surveyData = JSON.parse(surveyJsonString);
                } catch (parseError) {
                    dispatchNotification('settings.survey.error.invalidJSON', 'error');
                    setIsSaving(false);
                    return;
                }

                // Remember if the survey was originally inactive
                const wasOriginallyInactive = !isSurveyActive;

                // Save survey using the existing upsertSurvey API
                // NOTE: This will unfortunately make the survey active temporarily
                await performEnterpriseGPTApiCall(async (client) => {
                    await client.upsertSurvey(surveyData);
                });

                // If the survey was originally inactive, deactivate it immediately
                if (wasOriginallyInactive && currentSurvey?.id) {
                    const baseUrl = import.meta.env.VITE_MIDDLEWARE_API_BASE_URL;
                    const accessToken = await acquireAccessToken(appConfig.MIDDLEWARE_API_SCOPES);
                    
                    await fetch(`${baseUrl}/api/v1/survey/deactivate`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                        },
                        body: JSON.stringify({ surveyId: currentSurvey.id }),
                    });
                }

                dispatchNotification('settings.survey.save.success', 'success');

                // After saving, reload the survey in the same mode (inactive)
                await loadSurveyByMode(false);

            } catch (error) {
                console.error('Error saving survey:', error);
                dispatchNotification('settings.survey.error.saveFailed', 'error');
            } finally {
                setIsSaving(false);
            }
        },
        [dispatchNotification, loadSurveyByMode, isSurveyActive, currentSurvey],
    );

    // Function to handle survey activation/deactivation
    // This changes the database partition key: "latest" = active, "deactivated" = inactive
    const onSurveyActivationChange = useCallback(
        async (isActive: boolean) => {
            setIsSurveyActive(isActive);
            const baseUrl = import.meta.env.VITE_MIDDLEWARE_API_BASE_URL;
            const accessToken = await acquireAccessToken(appConfig.MIDDLEWARE_API_SCOPES);
            
            try {
                if (isActive) {
                    // Activate: set partitionKey to 'latest'
                    await fetch(`${baseUrl}/api/v1/survey/activate`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                        },
                        body: JSON.stringify(currentSurvey && currentSurvey.id ? { surveyId: currentSurvey.id } : {}),
                    });
                    
                    // Fetch the now-active survey
                    await loadSurveyByMode(true);
                    setIsSurveyActive(true);
                } else {
                    // Deactivate: set partitionKey to 'deactivated'
                    if (!currentSurvey) return;
                    
                    await fetch(`${baseUrl}/api/v1/survey/deactivate`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                        },
                        body: JSON.stringify({ surveyId: currentSurvey.id }),
                    });
                    
                    // Fetch the most recent survey (inactive)
                    await loadSurveyByMode(false);
                    setIsSurveyActive(false);
                }
            } catch (error) {
                // Optionally handle error
            }
        },
        [loadSurveyByMode, currentSurvey]
    );

    const handleThemeToggle = () => {
        themeDispatch(switchThemeAction());
    };

    const handleClearConversations = () => {
        confirm({
            message: '',
            show: true,
            title: t('menu.persona.confirmClearConversations'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    await client.deleteAllUserConversations(signal);
                    dispatch(clearConversationsAction());
                    dispatchNotification('settings.clearConversations.success', 'success');
                } catch {
                    dispatchNotification('error.api.delete.conversations', 'error');
                }
            });
        });
    };
    const handleDisenroll = () => {
        confirm({
            message: t('menu.persona.disenrollInfo'),
            show: true,
            title: t('menu.persona.disenrollConfirm'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, cancelToken) => {
                try {
                    await client.unenroll(cancelToken);
                    document.location.reload();
                } catch {
                    dispatchNotification('error.api.delete.enrollment', 'error');
                }
            });
        });
    };

    const handleLogout = () => {
        confirm({
            show: true,
            title: t('settings.logout.title', 'Log out of SyGPT'),
            message: t('settings.logout.confirm', 'Are you sure you want to log out?'),
            confirmText: t('Yes', 'Yes'),
            cancelText: t('Cancel', 'Cancel'),
        }).then(() => {
            logout();
        });
    };    // Create a wrapper function for the translation function to match expected signature
    const translationWrapper = (key: string, defaultValue?: string) => {
        return t(key, defaultValue || key);
    };

    // Filter and map speechToTextSupportedLanguages to ensure proper typing
    const mappedSpeechToTextLanguages = speechToTextSupportedLanguages
        .filter((lang): lang is Required<Pick<typeof lang, 'locale' | 'displayLanguage'>> => 
            Boolean(lang.locale && lang.displayLanguage)
        )
        .map(lang => ({
            locale: lang.locale!,
            displayLanguage: lang.displayLanguage!
        }));

    return (
        <SettingsView
            classes={classes}
            t={translationWrapper}
            theme={theme}
            LIGHT_THEME={LIGHT_THEME}
            DARK_THEME={DARK_THEME}
            DisenrollFromPlatformAvailable={DisenrollFromPlatformAvailable}
            selectedLanguage={selectedLanguage}
            speechToTextSupportedLanguages={mappedSpeechToTextLanguages}
            voiceEntryType={voiceEntryType}
            voiceEntryTypes={voiceEntryTypes}
            autoSendNumSecsInput={autoSendNumSecsInput}
            autoSendNumSecsError={autoSendNumSecsError}
            showLanguageSelectionInPrompt={showLanguageSelectionInPrompt}
            handleThemeToggle={handleThemeToggle}
            handleClearConversations={handleClearConversations}
            handleDisenroll={handleDisenroll}
            onUpdateUserPreferencesLanguage={onUpdateUserPreferencesLanguage}
            onUpdateVoiceEntryType={onUpdateVoiceEntryType}
            onUpdateAutoSendNumSecs={onUpdateAutoSendNumSecs}
            onUpdateShowLanguageSelectionInPrompt={onUpdateShowLanguageSelectionInPrompt}
            validateAutoSendNumSecs={validateAutoSendNumSecs}
            setAutoSendNumSecsInput={setAutoSendNumSecsInput}
            handleLogout={handleLogout}
            // Survey management props
            surveyManagementEnabled={isSurveyManagementEnabled}
            onFileUpload={onFileUpload}
            isUploading={isUploading}
            // Survey activation props
            isSurveyActive={isSurveyActive}
            onSurveyActivationChange={onSurveyActivationChange}
            // Survey data display
            currentSurvey={currentSurvey}
            isLoadingSurvey={isLoadingSurvey}
            // Survey editing props
            editedSurveyJson={editedSurveyJson}
            setEditedSurveyJson={setEditedSurveyJson}
            onSaveSurvey={onSaveSurvey}
            onSaveSurveyDirect={onSaveSurveyDirect}
            isSaving={isSaving}
        />
    );
};

export default SettingsHandler;
